// SPDX-License-Identifier: GPL-3.0-only
// Copyright (C) 2026 Kyle Johnson
// This file is part of Hushold.
//
// Hushold is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as
// published by the Free Software Foundation, version 3.

import { useCallback, useRef, useState } from 'react';
import { useApp } from '../store/AppContext.js';
import { fetchSyncLog, decryptBlobs } from '../lib/syncClient.js';

// Reusable "pull from the sync server, decrypt, merge" operation. Extracted so
// the mobile pull-to-refresh (Phase 4), the offline auto-resume (Phase 10), and
// any manual sync button share exactly one implementation and one in-flight guard.
export function useLedgerSync(worker: Worker | null, keyData: ArrayBuffer | null) {
  const { state, dispatch } = useApp();
  const [syncing, setSyncing] = useState(false);
  const inFlight = useRef(false);

  // Latest state via a ref so the callback stays stable but never reads a stale
  // lastSequence/token.
  const stateRef = useRef(state);
  stateRef.current = state;

  const sync = useCallback(async (): Promise<void> => {
    const { auth, ledger } = stateRef.current;
    if (!auth.token || !keyData || !worker || inFlight.current) return;
    inFlight.current = true;
    setSyncing(true);
    dispatch({ type: 'SYNC_START' });
    try {
      const config = { baseUrl: auth.syncUrl, token: auth.token };
      const blobs = await fetchSyncLog(config, ledger.lastSequence);
      if (blobs.length > 0) {
        const events = await decryptBlobs(blobs, worker, keyData);
        if (events.length > 0) {
          // Persisted as an encrypted snapshot by MainApp's effect (SEC-05).
          dispatch({ type: 'EVENTS_LOADED', events });
        }
      }
      dispatch({ type: 'SYNC_SUCCESS' });
    } catch (err) {
      dispatch({ type: 'SYNC_ERROR', message: err instanceof Error ? err.message : 'Sync failed' });
    } finally {
      inFlight.current = false;
      setSyncing(false);
    }
  }, [worker, keyData, dispatch]);

  return { sync, syncing };
}
